import React from 'react';
import { Box } from '@mui/material';
import { 
  Visibility as VisibilityIcon,
  CompareArrows as CompareArrowsIcon,
  DataObject as DataObjectIcon,
  SettingsSuggest as SettingsSuggestIcon,
  Rule as RuleIcon,
  TableChart as TableChartIcon,
  Transform as TransformIcon,
  Storage as StorageIcon,
  OpenInFull as OpenInFullIcon
} from '@mui/icons-material';

import ToolAppLayout from '../../layouts/ToolAppLayout';
import SEO, { createSoftwareAppSchema, createFAQSchema, createBreadcrumbSchema } from '../../components/SEO';

import JsonViewer from './JsonViewer';
import CsvToJson from './CsvToJson';
import XmlToJson from './XmlToJson';
import YamlToJson from './YamlToJson';
import JsonSchemaValidator from './JsonSchemaValidator';
import SqlToJson from './SqlToJson';
import JsonToCsv from './JsonToCsv';
import JsonToXml from './JsonToXml';
import JsonDiff from './JsonDiff';
import JsonToYaml from './JsonToYaml';
import JsonSchemaGenerator from './JsonSchemaGenerator';
import JsonPathTester from './JsonPathTester';
import LargeJsonViewer from './LargeJsonViewer';

const toolCategories = [
  {
    name: 'Format & View',
    tools: [
      { name: 'JSON Viewer', description: 'Format, beautify, and explore JSON in a tree view', path: 'viewer', icon: <VisibilityIcon fontSize="small" />, component: JsonViewer },
      { name: 'Large JSON Viewer', description: 'Optimized viewer for massive JSON files', path: 'large-viewer', icon: <OpenInFullIcon fontSize="small" />, component: LargeJsonViewer },
      { name: 'JSON Diff', description: 'Compare two JSON objects side-by-side', path: 'diff', icon: <CompareArrowsIcon fontSize="small" />, component: JsonDiff },
    ]
  },
  {
    name: 'Schema & Query',
    tools: [
      { name: 'Schema Generator', description: 'Generate JSON Schema from sample data', path: 'schema-generator', icon: <SettingsSuggestIcon fontSize="small" />, component: JsonSchemaGenerator },
      { name: 'Schema Validator', description: 'Validate JSON against a JSON Schema', path: 'schema-validator', icon: <RuleIcon fontSize="small" />, component: JsonSchemaValidator },
      { name: 'JSONPath Tester', description: 'Test and evaluate JSONPath expressions', path: 'jsonpath', icon: <DataObjectIcon fontSize="small" />, component: JsonPathTester },
    ]
  },
  {
    name: 'Data Conversion',
    tools: [
      { name: 'CSV to JSON', description: 'Convert tabular CSV data into JSON arrays', path: 'csv-to-json', icon: <TableChartIcon fontSize="small" />, component: CsvToJson },
      { name: 'JSON to CSV', description: 'Convert JSON arrays into tabular CSV format', path: 'json-to-csv', icon: <TableChartIcon fontSize="small" />, component: JsonToCsv },
      { name: 'XML to JSON', description: 'Convert XML documents into JSON', path: 'xml-to-json', icon: <TransformIcon fontSize="small" />, component: XmlToJson },
      { name: 'JSON to XML', description: 'Convert JSON into valid XML documents', path: 'json-to-xml', icon: <TransformIcon fontSize="small" />, component: JsonToXml },
      { name: 'YAML to JSON', description: 'Convert YAML configuration files to JSON', path: 'yaml-converter', icon: <TransformIcon fontSize="small" />, component: YamlToJson },
      { name: 'JSON to YAML', description: 'Convert JSON into YAML configuration files', path: 'json-to-yaml', icon: <TransformIcon fontSize="small" />, component: JsonToYaml },
      { name: 'SQL to JSON', description: 'Convert SQL table definitions to JSON', path: 'sql-to-json', icon: <StorageIcon fontSize="small" />, component: SqlToJson },
    ]
  }
];

// SEO: Structured Data for JSON Tools
const jsonToolsSchema = createSoftwareAppSchema(
  'Free Online JSON Tools - Viewer, Formatter, Converter',
  'Free online JSON tools: validate, format, beautify, and convert JSON. Includes JSON viewer, CSV to JSON, XML to JSON, YAML converter, JSON Schema validator, and more.',
  'https://umbrellaco.in/json-tools',
  [
    'JSON Viewer and Formatter',
    'Large JSON Viewer',
    'JSON Diff Tool',
    'JSONPath Evaluator',
    'JSON Schema Generator',
    'JSON Beautifier and Minifier',
    'CSV to JSON Converter',
    'JSON to CSV Converter',
    'XML to JSON Converter',
    'JSON to XML Converter',
    'YAML to JSON Converter',
    'JSON to YAML Converter',
    'JSON Schema Validator',
    'SQL to JSON Converter'
  ]
);

// SEO: FAQ Schema for rich snippets
const faqSchema = createFAQSchema([
  {
    question: 'How do I format JSON online for free?',
    answer: 'Use our free JSON Viewer tool to paste your JSON, and it will automatically format and beautify it with proper indentation. You can also validate JSON syntax and view it in a tree structure.'
  },
  {
    question: 'How to convert CSV to JSON online?',
    answer: 'Use our CSV to JSON converter: paste your CSV data, and it instantly converts to valid JSON format. The tool handles headers automatically and supports various delimiters.'
  },
  {
    question: 'What is a JSON Schema Validator?',
    answer: 'A JSON Schema Validator checks if your JSON data conforms to a defined schema. Our free validator helps you validate JSON structure, data types, and required fields against JSON Schema standards.'
  },
  {
    question: 'How to convert XML to JSON?',
    answer: 'Our XML to JSON converter parses your XML and transforms it to equivalent JSON structure. Simply paste your XML, and get clean JSON output instantly.'
  }
]);

// SEO: Breadcrumb Schema
const breadcrumbSchema = createBreadcrumbSchema([
  { name: 'Home', url: 'https://umbrellaco.in' },
  { name: 'JSON Tools', url: 'https://umbrellaco.in/json-tools' }
]);

// Combine all schemas
const combinedSchema = [jsonToolsSchema, faqSchema, breadcrumbSchema];

function JsonToolsApp() {
  return (
    <>
      <SEO
        title="Free Online JSON Tools - Viewer, Formatter, Converter | UmbrellaCo"
        description="Free online JSON tools: validate, format, beautify JSON instantly. Convert CSV to JSON, XML to JSON, YAML to JSON. JSON Schema validator, JSON viewer with tree view. No signup required."
        keywords="json viewer, json formatter, json beautifier, json validator, csv to json, json to csv, xml to json, json to xml, yaml to json, json schema validator, online json tools, free json formatter, json parser online, json editor online"
        url="/json-tools"
        type="website"
        jsonLd={combinedSchema}
      />
      <ToolAppLayout
        title="JSON Tools"
        description="Format, validate, query, and convert JSON data effortlessly."
        toolCategories={toolCategories}
        basePath="/json-tools"
      />
    </>
  );
}

export default JsonToolsApp;
